import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallow } from "zustand/shallow";
import { useArticleImportStore } from "../../stores/article_import";
import { useToastStore } from "../../stores/toast";

const POLL_INTERVAL_MS = 3000;

function ArticleImportTab() {
  const { t } = useTranslation();
  const addToast = useToastStore((s) => s.addToast);

  const [status, triggering, fetchStatus, trigger] = useArticleImportStore(
    (s) => [s.status, s.triggering, s.fetchStatus, s.trigger],
    shallow,
  );

  const [errorsOpen, setErrorsOpen] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status?.running) {
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status?.running, fetchStatus]);

  const handleTrigger = async () => {
    const { error, status: httpStatus } = await trigger({ updateExisting });
    if (httpStatus === 409) {
      addToast("warning", t("settings.article_import.already_running"));
    } else if (error) {
      addToast("error", t("common.error"));
    } else {
      addToast("success", t("settings.article_import.started"));
    }
    fetchStatus();
  };

  const result = status?.last_result || null;
  const errors = result?.errors || [];
  const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <>
      <h5 className="card-title">{t("settings.article_import.title")}</h5>
      <p className="text-muted">{t("settings.article_import.description")}</p>

      {status?.running && (
        <div className="alert alert-info">
          <i className="fa fa-spinner fa-spin me-2" />
          {t("settings.article_import.running")}
        </div>
      )}

      {result?.error && (
        <div className="alert alert-warning">
          {t(`settings.article_import.error.${result.error}`, {
            defaultValue: result.error,
          })}
        </div>
      )}

      {result && !result.error && (
        <div className="row mb-3">
          <div className="col-6 col-md-3">
            <div className="text-muted small">
              {t("settings.article_import.counts.created")}
            </div>
            <div className="h4">{result.created ?? 0}</div>
          </div>
          <div className="col-6 col-md-3">
            <div className="text-muted small">
              {t("settings.article_import.counts.updated")}
            </div>
            <div className="h4">{result.updated ?? 0}</div>
          </div>
          <div className="col-6 col-md-3">
            <div className="text-muted small">
              {t("settings.article_import.counts.skipped")}
            </div>
            <div className="h4">{result.skipped ?? 0}</div>
          </div>
          <div className="col-6 col-md-3">
            <div className="text-muted small">
              {t("settings.article_import.counts.errors")}
            </div>
            <div className="h4">{result.error_count ?? 0}</div>
          </div>
        </div>
      )}

      {result && !result.error && (
        <div className="row mb-3 small text-muted">
          <div className="col-md-6">
            {t("settings.article_import.started_at")}: {formatDateTime(result.started_at)}
          </div>
          <div className="col-md-6">
            {t("settings.article_import.finished_at")}: {formatDateTime(result.finished_at)}
          </div>
          {result.duration_seconds != null && (
            <div className="col-md-6">
              {t("settings.article_import.duration")}: {result.duration_seconds}s
            </div>
          )}
          {result.pages != null && (
            <div className="col-md-6">
              {t("settings.article_import.pages")}: {result.pages}
            </div>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <div className="mb-3">
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={() => setErrorsOpen(!errorsOpen)}
          >
            <i className={`fa fa-chevron-${errorsOpen ? "down" : "right"} me-1`} />
            {t("settings.article_import.show_errors", { count: errors.length })}
          </button>
          {errorsOpen && (
            <ul className="list-unstyled mt-2 small">
              {errors.map((e, i) => (
                <li key={i}>
                  <code>{e.number}</code>: {e.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!result && !status?.running && (
        <div className="alert alert-secondary">
          {t("settings.article_import.empty_state")}
        </div>
      )}

      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="article-import-update-existing"
          checked={updateExisting}
          onChange={(e) => setUpdateExisting(e.target.checked)}
          disabled={triggering || status?.running}
        />
        <label
          className="form-check-label"
          htmlFor="article-import-update-existing"
        >
          {t("settings.article_import.update_existing")}
        </label>
        <div className="form-text">
          {t("settings.article_import.update_existing_hint")}
        </div>
      </div>
      <button
        type="button"
        className="btn btn-success"
        disabled={triggering || status?.running}
        onClick={handleTrigger}
      >
        {(triggering || status?.running) && (
          <i className="fa fa-spinner fa-spin me-1" />
        )}
        {t("settings.article_import.start")}
      </button>
    </>
  );
}

export default ArticleImportTab;
